import React, { useState } from "react";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import { toast } from "sonner";
import { Baby, Mail, Link as LinkIcon, Copy } from "lucide-react";

export default function KidsBecomeCreator() {
  const { user } = useAuth();
  const [parentEmail, setParentEmail] = useState("");
  const [invite, setInvite] = useState(null);
  const [busy, setBusy] = useState(false);

  if (!user) {
    return <div className="pt-32 text-center text-white/70">Please sign in first.</div>;
  }

  const submit = async (e) => {
    e.preventDefault();
    if (!parentEmail.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post("/kids/invite-parent", { parent_email: parentEmail.trim(), origin_url: window.location.origin });
      setInvite(data);
      toast.success(data.email_sent ? "Email sent to parent" : "Invite created");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  const copyLink = () => {
    const link = window.location.origin + invite.invite_url;
    navigator.clipboard?.writeText(link);
    toast.success("Invite link copied");
  };

  return (
    <div data-testid="kids-become-page" className="pt-8 pb-20 px-4 flex justify-center">
      <div className="tv-glass rounded-2xl w-full max-w-xl p-6 md:p-8">
        <img
          src="/tapintv-kids-logo.png"
          alt="Tap IN Kids Network"
          data-testid="kids-logo-become"
          className="w-36 sm:w-44 mx-auto mb-4 drop-shadow-[0_0_30px_rgba(250,204,21,0.35)]"
        />
        <div className="flex items-center gap-2 mb-2 justify-center">
          <Baby className="w-6 h-6 text-yellow-300" />
          <div className="text-xs uppercase tracking-[0.3em] text-yellow-200 font-bold">Become a Kid Creator</div>
        </div>
        <h1 className="font-display font-black text-3xl md:text-4xl text-white tracking-tight text-center">Step 1 of 2</h1>
        <p className="text-white/75 mt-2 text-sm text-center">Enter a parent or guardian's email. They'll receive a secure link to create a monitoring account and sign consent before you can start uploading.</p>

        {!invite ? (
          <form onSubmit={submit} className="mt-6 space-y-3">
            <div>
              <label className="text-xs uppercase tracking-wider text-white/60 font-semibold flex items-center gap-1"><Mail className="w-3 h-3" /> Parent / guardian email</label>
              <input
                data-testid="kids-parent-email-input"
                type="email"
                value={parentEmail}
                onChange={(e) => setParentEmail(e.target.value)}
                placeholder="parent@example.com"
                className="mt-1 w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 text-white outline-none focus:border-tv-brand"
                required
              />
            </div>
            <button
              data-testid="kids-invite-submit"
              type="submit"
              disabled={busy}
              className="w-full bg-yellow-300 hover:bg-yellow-200 text-black font-bold py-3 rounded-full disabled:opacity-60"
            >
              {busy ? "Creating invite…" : "Send Parent Invite"}
            </button>
          </form>
        ) : (
          <div data-testid="kids-invite-success" className="mt-6 space-y-3">
            <div className="text-green-400 font-semibold">
              {invite.email_sent ? "Invite email sent!" : "Invite created"}
            </div>
            <p className="text-white/70 text-sm">
              {invite.email_sent
                ? `We emailed ${parentEmail}. If it doesn't arrive in a few minutes, share the link below directly.`
                : `Please share this link with your parent directly (email delivery to this address isn't enabled yet).`}
            </p>
            <div className="flex items-center gap-2 bg-black/40 rounded-lg p-3 border border-white/10">
              <LinkIcon className="w-4 h-4 text-white/60" />
              <code className="flex-1 text-xs text-white/90 break-all" data-testid="kids-invite-link">
                {window.location.origin + invite.invite_url}
              </code>
              <button onClick={copyLink} className="text-tv-brand hover:text-tv-accent" data-testid="kids-copy-link-btn"><Copy className="w-4 h-4" /></button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
